import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IQuestionnaire,
	IGetQuestionnairesThunkArgs,
	ICreateQuestionnaireSubmissionThunkArgs,
} from 'src/features/questionnaire/domain/types';
import { questionnaireApi } from 'src/features/questionnaire/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const QUESTIONNAIRE_LOADING_KEY = 'questionnaire';

export const getQuestionnaireSubmission = createAsyncThunk<
	IQuestionnaire,
	IGetQuestionnairesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire/getQuestionnaires',
	async (
		{ questionnaireId, studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: QUESTIONNAIRE_LOADING_KEY, loading: true }),
			);
			const response = await questionnaireApi.getQuestionnaireSubmission(
				questionnaireId,
				studyId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch questionnaires',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: QUESTIONNAIRE_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const createQuestionnaireSubmission = createAsyncThunk<
	void,
	ICreateQuestionnaireSubmissionThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire/createQuestionnaireSubmission',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({ key: QUESTIONNAIRE_LOADING_KEY, loading: true }),
			);
			const response =
				await questionnaireApi.createQuestionnaireSubmission(
					request,
					siteSlug,
				);

			onSuccess?.();
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create questionnaire submission',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: QUESTIONNAIRE_LOADING_KEY, loading: false }),
			);
		}
	},
);
